/**
 * CollabRequestModal – detalj-side for én konkret samarbeid-avtale.
 * Viser status, promo-pakke (kopier/last-ned), handlinger basert på rolle.
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Copy, Download, CheckCircle, Upload, AlertTriangle, Package } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../lib/api";
import { AuthImage, authDownload } from "./AuthMedia";

const STATUS_LABEL = {
  proposed: "Foreslått",
  accepted: "Akseptert",
  posted: "Postet",
  confirmed: "Bekreftet",
  declined: "Avslått",
  cancelled: "Avlyst",
};

export default function CollabRequestModal({ requestId, onClose }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [disputing, setDisputing] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  const [pickingPacket, setPickingPacket] = useState(false);
  const [myPackets, setMyPackets] = useState([]);
  const [suggestedPackets, setSuggestedPackets] = useState([]);
  const [hasStrictMatch, setHasStrictMatch] = useState(false);

  const openPacketPicker = async () => {
    // Hent alle + match-resultater
    const [all, matches] = await Promise.all([
      api.get("/collab/promo-packets").catch(() => ({ data: { results: [] } })),
      req
        ? api.get(`/collab/promo-packets/match?date=${req.scheduled_date}&account_ids=${(req.account_ids || []).join(",")}&platforms=${(req.platforms || []).join(",")}`).catch(() => ({ data: { results: [], has_strict_match: false } }))
        : Promise.resolve({ data: { results: [], has_strict_match: false } }),
    ]);
    setMyPackets(all.data?.results || []);
    setSuggestedPackets(matches.data?.results || []);
    setHasStrictMatch(!!matches.data?.has_strict_match);
    setPickingPacket(true);
  };

  const attachPacket = async (packet_id) => {
    try {
      await api.post(`/collab/requests/${requestId}/attach-packet`, { packet_id });
      toast.success(t("collab.packet_attached"));
      setPickingPacket(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("collab.save_err"));
    }
  };

  const load = () => {
    setLoading(true);
    // Bruk list-endpoint og filter siden vi ikke har get-by-id
    api.get("/collab/requests?direction=both")
      .then((r) => {
        const found = (r.data?.results || []).find((x) => x.id === requestId);
        setReq(found || null);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, [requestId]);

  if (loading) return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="text-[var(--cream)]">{t("common.loading")}</div>
    </div>
  );
  if (!req) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
        <div className="panel-tight p-6 text-[var(--cream)]">
          {t("collab.not_found")}
        </div>
      </div>
    );
  }

  const isRequester = user.id === req.requester_id;
  const otherUsername = isRequester ? req.target_username : req.requester_username;
  const packet = req.promo_packet;
  const targetPacket = req.target_promo_packet;
  // Min pakke avhengig av rolle:
  const myPacket = isRequester ? packet : targetPacket;
  const myFieldHas = !!myPacket;

  const act = async (action, extra = {}) => {
    try {
      await api.post(`/collab/requests/${req.id}/${action}`, extra);
      toast.success(t(`collab.action_${action}_ok`));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("collab.save_err"));
    }
  };

  const copyText = (txt) => {
    // OnlyFans/X-editorer strimler tomme linjer (`\n\n` → `\n`). Legg til en
    // non-breaking space på "tomme" linjer så editorene ser innhold og beholder
    // linjeskiftet. Usynlig for menneskelig lesing.
    const safe = (txt || "").replace(/\n\n/g, "\n\u00A0\n");
    navigator.clipboard.writeText(safe).then(() => toast.success(t("collab.copied")));
  };

  const uploadProof = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/collab/requests/${req.id}/upload-proof`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("collab.proof_uploaded"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("collab.save_err"));
    }
  };

  const submitDispute = async () => {
    if (disputeReason.trim().length < 10) {
      toast.error(t("collab.dispute_need_reason"));
      return;
    }
    try {
      await api.post(`/collab/requests/${req.id}/dispute`, { reason: disputeReason });
      toast.success(t("collab.dispute_filed"));
      setDisputing(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("collab.save_err"));
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
      onClick={onClose} data-testid="request-modal">
      <div className="max-w-2xl w-full panel-tight relative my-8 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-[var(--bg)] border-b border-white/10 px-6 pt-6 pb-4">
          <button onClick={onClose} className="absolute right-3 top-3 text-[var(--cream-faint)] hover:text-[var(--magenta)]" data-testid="request-modal-close">
            <X className="h-4 w-4" />
          </button>
          <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-1">
            {req.type.toUpperCase()} · {STATUS_LABEL[req.status]?.toUpperCase() || req.status.toUpperCase()}
          </div>
          <h2 className="font-fraunces italic text-2xl text-[var(--cream)] mb-1">
            {isRequester ? t("collab.you_x_target", { target: otherUsername }) : t("collab.x_you", { src: otherUsername })}
          </h2>
        <div className="text-sm text-[var(--cream-soft)] mb-4">
          {req.scheduled_date} · {(req.platforms || []).map(p => p.toUpperCase()).join(" / ")}
        </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">

        {req.message && (
          <div className="pl-4 border-l-2 border-white/15 text-sm text-[var(--cream-soft)] mb-4 whitespace-pre-line">
            {req.message}
          </div>
        )}

        {/* Promo-pakker fra begge sider */}
        {packet && <PacketView packet={packet} role="requester" otherUsername={req.requester_username} copyText={copyText} t={t} />}
        {targetPacket && <PacketView packet={targetPacket} role="target" otherUsername={req.target_username} copyText={copyText} t={t} />}

        {/* Legg ved egen pakke (hvis ikke alt har) */}
        {!myFieldHas && ["proposed", "accepted", "posted"].includes(req.status) && (
          <button onClick={openPacketPicker}
            className="mb-4 px-3 py-2 border border-[var(--magenta)]/40 text-[var(--magenta)] hover:bg-[var(--magenta)]/10 font-meta text-xs inline-flex items-center gap-1.5"
            data-testid="attach-packet-btn">
            <Package className="h-3.5 w-3.5" /> {t("collab.attach_my_packet")}
          </button>
        )}
        {myFieldHas && ["proposed", "accepted", "posted"].includes(req.status) && (
          <button onClick={openPacketPicker}
            className="mb-4 px-3 py-2 border border-white/15 text-[var(--cream-soft)] hover:border-white/30 font-meta text-xs inline-flex items-center gap-1.5"
            data-testid="change-packet-btn">
            <Package className="h-3.5 w-3.5" /> {t("collab.change_my_packet")}
          </button>
        )}

        {pickingPacket && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={() => setPickingPacket(false)} data-testid="packet-picker">
            <div className="max-w-md w-full panel-tight p-5" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-fraunces italic text-xl text-[var(--cream)] mb-3">{t("collab.pick_packet")}</h3>
              {hasStrictMatch ? (
                <>
                  <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--gold)] mb-2">{t("collab.matched_packet_for_date")}</div>
                  <ul className="space-y-2">
                    {suggestedPackets.map(({ packet: p }) => (
                      <li key={p.id}>
                        <button onClick={() => attachPacket(p.id)}
                          className="w-full text-left p-3 border border-[var(--gold)]/40 hover:border-[var(--gold)] bg-[var(--gold)]/5 text-[var(--cream)]"
                          data-testid={`pick-suggested-${p.id}`}>
                          <div className="font-fraunces italic">{p.title}</div>
                          <div className="text-xs text-[var(--cream-faint)] mt-0.5">
                            {p.text_variants?.length || 0} {t("collab.text_variants")} · {p.media_urls?.length || 0} {t("collab.media_files")}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              ) : myPackets.length === 0 ? (
                <p className="text-sm text-[var(--cream-faint)]">{t("collab.no_packets_yet")}</p>
              ) : (
                <ul className="space-y-2">
                  {myPackets.map((p) => (
                    <li key={p.id}>
                      <button onClick={() => attachPacket(p.id)}
                        className="w-full text-left p-3 border border-white/10 hover:border-[var(--magenta)] text-[var(--cream)]"
                        data-testid={`pick-packet-${p.id}`}>
                        <div className="font-fraunces italic">{p.title}</div>
                        <div className="text-xs text-[var(--cream-faint)] mt-0.5">
                          {p.text_variants?.length || 0} {t("collab.text_variants")} · {p.media_urls?.length || 0} {t("collab.media_files")}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Bevis-bilde */}
        {req.proof_url && (
          <div className="mb-4">
            <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-2">{t("collab.proof_label")}</div>
            <AuthImage url={req.proof_url} alt="" className="max-w-xs border border-white/10" />
          </div>
        )}

        {/* Handlinger */}
        <div className="flex flex-wrap gap-2">
          {req.status === "proposed" && !isRequester && (
            <>
              <button onClick={() => act("accept", {})} className="btn-magenta" data-testid="action-accept">
                {t("collab.accept")}
              </button>
              <button onClick={() => act("decline", {})} className="px-3 py-2 border border-red-400/40 text-red-300 hover:bg-red-400/10 font-meta text-xs">
                {t("collab.decline")}
              </button>
            </>
          )}
          {(req.status === "accepted" || req.status === "posted" || req.status === "confirmed") && (() => {
            const isReq = req.requester_id === user?.id;
            const mine = isReq ? "requester" : "target";
            const myPosted = req[`${mine}_posted_at`];
            const myProof = req[`${mine}_proof_url`];
            return (
              <>
                {!myPosted && (
                  <button onClick={() => act("mark-posted", {})} className="btn-magenta" data-testid="action-mark-posted">
                    {t("collab.mark_posted")}
                  </button>
                )}
                {!myProof && (
                  <label className="px-3 py-2 border border-white/15 text-[var(--cream-soft)] cursor-pointer hover:border-white/30 font-meta text-xs inline-flex items-center gap-1.5">
                    <Upload className="h-3.5 w-3.5" /> {t("collab.upload_proof")}
                    <input type="file" accept="image/*" className="hidden"
                      onChange={(e) => uploadProof(e.target.files?.[0])} data-testid="action-upload-proof" />
                  </label>
                )}
              </>
            );
          })()}
          {req.status === "posted" && req.posted_by !== user.id && (() => {
            const isReq = req.requester_id === user?.id;
            const mine = isReq ? "requester" : "target";
            const myConf = req[`${mine}_confirmed_at`];
            if (myConf) return null;
            return (
              <button onClick={() => act("confirm", {})} className="btn-magenta inline-flex items-center gap-1.5" data-testid="action-confirm">
                <CheckCircle className="h-4 w-4" /> {t("collab.confirm_seen")}
              </button>
            );
          })()}
          {req.status === "posted" && (
            <button onClick={() => setDisputing(!disputing)} className="px-3 py-2 border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 font-meta text-xs inline-flex items-center gap-1.5"
              data-testid="action-dispute">
              <AlertTriangle className="h-3.5 w-3.5" /> {t("collab.flag_dispute")}
            </button>
          )}
          {["proposed", "accepted"].includes(req.status) && (
            <button onClick={() => act("cancel", {})} className="px-3 py-2 border border-white/15 text-[var(--cream-soft)] font-meta text-xs hover:border-white/30">
              {t("collab.cancel")}
            </button>
          )}
        </div>

        {disputing && (
          <div className="mt-4 panel p-4 border border-amber-400/30" data-testid="dispute-form">
            <div className="font-meta text-[10px] tracking-[0.2em] text-amber-300 mb-2">{t("collab.dispute_label")}</div>
            <p className="text-xs text-[var(--cream-soft)] mb-2">{t("collab.dispute_hint")}</p>
            <textarea value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} rows={3} maxLength={1000}
              className="input-editorial w-full font-manrope text-sm" data-testid="dispute-reason" />
            <button onClick={submitDispute} className="btn-magenta mt-2" data-testid="dispute-submit">{t("collab.submit_dispute")}</button>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}


function PacketView({ packet, role, otherUsername, copyText, t }) {
  const [open, setOpen] = useState(false);
  const text = (packet.text_variants || [])[0] || "";
  const mediaCount = packet.media_urls?.length || 0;
  const downloadAll = () => {
    (packet.media_urls || []).forEach((url, i) => {
      authDownload(url, `${packet.title || "media"}-${i + 1}`);
    });
  };
  return (
    <div className="border border-white/10 mb-4" data-testid={`packet-${role}`}>
      <button onClick={() => setOpen(!open)} type="button"
        className="w-full flex items-center justify-between gap-2 px-4 py-3 hover:bg-white/[0.02]">
        <div className="text-left">
          <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)]">
            {role === "requester" ? t("collab.packet_from_requester") : t("collab.packet_from_target")}
            <span className="text-[var(--cream-faint)] ml-1">· @{otherUsername}</span>
          </div>
          <div className="font-fraunces italic text-[var(--cream)] mt-0.5">{packet.title}</div>
          <div className="text-xs text-[var(--cream-faint)] mt-0.5">
            {text ? "📝 " + t("collab.has_text") : t("collab.no_text")} · {mediaCount} {t("collab.media_files")}
          </div>
        </div>
        <span className="font-meta text-[10px] text-[var(--magenta)]">{open ? t("collab.hide") : t("collab.show")}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            {text.trim() && (
              <button onClick={() => copyText(text)} type="button"
                className="px-3 py-1.5 border border-[var(--magenta)]/40 text-[var(--magenta)] hover:bg-[var(--magenta)]/10 font-meta text-[11px] inline-flex items-center gap-1.5"
                data-testid="copy-text">
                <Copy className="h-3 w-3" /> {t("collab.copy_text")}
              </button>
            )}
            {mediaCount > 0 && (
              <button onClick={downloadAll} type="button"
                className="px-3 py-1.5 border border-[var(--magenta)]/40 text-[var(--magenta)] hover:bg-[var(--magenta)]/10 font-meta text-[11px] inline-flex items-center gap-1.5"
                data-testid="download-all-media">
                <Download className="h-3 w-3" /> {t("collab.download_all_media")} ({mediaCount})
              </button>
            )}
          </div>
          {text && (
            <div className="bg-black/30 p-3 border border-white/10">
              <p className="text-sm text-[var(--cream)] whitespace-pre-line">{text}</p>
            </div>
          )}
          {mediaCount > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {packet.media_urls.map((url, i) => (
                <button key={i} type="button"
                  onClick={() => authDownload(url, `${packet.title || "media"}-${i + 1}`)}
                  className="block relative aspect-square bg-black/40 border border-white/10 hover:border-[var(--magenta)] group overflow-hidden">
                  <AuthImage url={url} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100">
                    <Download className="h-5 w-5 text-[var(--magenta)]" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
